import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { LazyResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { MarkdownEditor } from "components/Form/components/EditableMarkdown/MarkdownEditor";
import { useState, useCallback } from "react";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import style from "./EditableMarkdown.module.scss";

type MarkdownViewMode = "preview" | "text";

/**
 * Renders a MARKDOWN value as interactive rendered markdown by default
 * ("preview": links are clickable and text is selectable), with editing reached
 * either through a single "edit" icon button (schema-form attributes) or, when
 * `editOnClick` is set (the record `description`), by clicking the field box
 * itself — a bordered light-grey box that reads and edits with no size/position
 * jump (ui-design-principle-edit-fields).
 *
 * A click on a link or an active text selection does NOT enter edit, so the two
 * things you normally do with rendered markdown (follow a link, select/copy text)
 * still work. The plain-text ("edit") view is a Monaco editor configured for
 * markdown (syntax highlighting), filling the full available width.
 */
export const EditableMarkdownComponent = ({
    inputProps,
    attributes,
    className,
    isReadOnly,
    isCollapsible,
    hugWhenEmpty,
    // When true (the record `description`), the field renders and edits inside one
    // bordered light-grey box and enters edit mode on click of the box — no
    // separate pencil button, no reflow. Only the record header description sets
    // this; schema-form markdown attributes keep the pencil-toggle affordance.
    // (operator-task-fe-record-description-edit-view-redesign)
    editOnClick = false,
    // When true, the edit view uses the heavier shared bordered/resizable
    // CodeEditor wrapper (line-number gutter, resize handle, fullscreen toggle)
    // that the other schema-form code fields use. This is appropriate ONLY for
    // editing a markdown ATTRIBUTE in a full open-record schema form. In compact
    // contexts — the record `description` field and markdown editing inside a
    // table/list-cell popover — that chrome is oversized and reads as an
    // out-of-place code editor (Margot, 2026-08-18), so those default to the
    // lightweight MarkdownEditor instead.
    // (operator-task-fe-markdown-codeeditor-border-and-fullscreen-toggle)
    useCodeEditor = false,
}: any) => {
    const [mode, setMode] = useState<MarkdownViewMode>("preview");
    const [isExpanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);

    const markdownRef = useCallback(
        (node) => {
            if (node !== null) {
                if (isCollapsible) {
                    setCanExpand(node.scrollHeight - 27 > Number(style.initialHeight));
                }
            }
        },
        [isCollapsible]
    );

    // Editing is only possible when the field is not read-only. When read-only
    // we ignore any stored mode and always render the preview.
    const isEditing = !isReadOnly && mode === "text";
    // The boxed click-to-edit affordance only applies to an editable field.
    const isBoxed = !!editOnClick && !isReadOnly;
    const isEmptyPlaceholder = !!attributes?.placeholder && !inputProps?.value;

    // Enter edit on a click of the box — but never hijack a link/button click or
    // an active text selection (both are normal things to do on rendered markdown).
    const enterEdit = useCallback((event) => {
        const target = event?.target as HTMLElement | undefined;
        if (target?.closest && target.closest("a, button")) {
            return;
        }
        const selection =
            typeof window !== "undefined" && window.getSelection ? String(window.getSelection()) : "";
        if (selection) {
            return;
        }
        setMode("text");
    }, []);

    // A single icon button (no label) toggles between the rendered preview and
    // the plain-text editor. Used by the non-boxed (schema-form) affordance.
    const editToggleButton = isReadOnly ? null : (
        <Button
            className={style.editToggleButton}
            variation="white"
            labelOnlyAria
            ariaLabel={isEditing ? "Preview markdown" : "Edit markdown"}
            title={isEditing ? "Preview" : "Edit"}
            icon={isEditing ? IconCollection.eyeOutline : IconCollection.pencilOutline}
            onClick={() => setMode(isEditing ? "preview" : "text")}
        />
    );

    // The plain-text editor. In the boxed variant, blurring out of the editor
    // returns to preview (click-away-to-finish), matching the field's old feel.
    const editor = useCodeEditor ? (
        <LazyResizeableCodeEditor
            input={{
                name: "editableMarkdownEditor",
                value: inputProps.value ?? "",
                onChange: (value) => inputProps.onChange?.(value),
                onFocus: () => {},
                onBlur: () => inputProps.onBlur?.(),
            }}
            fieldAttr={{ disabled: attributes?.disabled }}
            meta={{}}
            fieldProps={{
                lang: "markdown",
                label: "",
                isReadOnly: !!attributes?.disabled,
                initialHeight: 200,
                minHeight: 150,
            }}
        />
    ) : (
        <MarkdownEditor
            value={inputProps.value ?? ""}
            onChange={(next) => inputProps.onChange?.(next)}
            onBlur={() => {
                inputProps.onBlur?.();
                if (isBoxed) {
                    setMode("preview");
                }
            }}
            autoFocus
            readOnly={attributes?.disabled}
        />
    );

    // The rendered (preview) content, shared by every non-editing render path:
    // a clip wrapper measured for the collapse threshold, a soft fade cue over
    // the clipped bottom, and a quiet "Show more/less" text link.
    const collapsibleView = (
        <>
            <div className={canExpand && !isExpanded ? `${style.clipWrap} ${style.clipWrapCollapsed}` : style.clipWrap}>
                <div ref={markdownRef} className={style.contentInner}>
                    {isEmptyPlaceholder ? (
                        <span className={style.markdownPlaceholder}>{attributes?.placeholder}</span>
                    ) : (
                        <CustomLinkMarkdown markdownString={inputProps.value ?? ""} />
                    )}
                </div>
                {isCollapsible && canExpand && !isExpanded && <div className={style.fadeOverlay} />}
            </div>

            {isCollapsible && canExpand && (
                <div
                    className={style.showMoreLink}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                        event.stopPropagation();
                        setExpanded((prev) => !prev);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.stopPropagation();
                            event.preventDefault();
                            setExpanded((prev) => !prev);
                        }
                    }}
                >
                    <Button
                        label={`Show ${isExpanded ? "less" : "more"}`}
                        icon={isExpanded ? IconCollection.chevronUp : IconCollection.chevronDown}
                        variation="clear"
                        size="small"
                    />
                </div>
            )}
        </>
    );

    // Boxed edit-on-click variant (record description): one bordered box that
    // reads and edits in place.
    if (isBoxed) {
        if (isEditing) {
            return (
                <div className={`${style.boxedField} ${style.boxedFieldEditing}`}>
                    {editor}
                    <Button
                        className={style.boxedToggle}
                        variation="clear"
                        labelOnlyAria
                        ariaLabel="Preview markdown"
                        title="Preview"
                        icon={IconCollection.eyeOutline}
                        onClick={() => setMode("preview")}
                    />
                </div>
            );
        }
        return (
            <div
                className={`${className ?? ""} ${style.boxedField} ${style.boxedFieldView}`}
                role="button"
                tabIndex={0}
                title="Click to edit"
                onClick={enterEdit}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        setMode("text");
                    }
                }}
            >
                {collapsibleView}
            </div>
        );
    }

    const content = isEditing ? editor : collapsibleView;

    // Read-only: no editor affordance — render the content exactly as before.
    if (isReadOnly) {
        return <div className={className}>{content}</div>;
    }

    // Editable (non-boxed, schema-form markdown attribute): the content fills the
    // available width, with the edit / preview icon button at the right end of the
    // row so it groups with the reset-to-default / clear-field action buttons.
    const contentClassName = `${className ?? ""} ${style.editableMarkdownContent} ${
        hugWhenEmpty && isEmptyPlaceholder ? style.editableMarkdownContentHugEmpty : ""
    }`;
    return (
        <div className={style.editableMarkdownRow}>
            <div className={contentClassName}>{content}</div>
            {editToggleButton}
        </div>
    );
};
